import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import toast from "react-hot-toast";

const baseURL = import.meta.env.VITE_API_BASE_URL;
// ---------------------------------------------------------
export const axiosInstance = axios.create({
  baseURL,
});

// Add token automatically to EVERY request
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("usertoken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    config.headers["X-API-KEY"] =
      "Gt9X2JvGVMuzTu2E6eO023zLNc98OpaXAQZY64ujnATHRunIqYr2JEaGhGMr";

    // Only set JSON if the body is NOT FormData
    if (!(config.data instanceof FormData)) {
      config.headers["Content-Type"] = "application/json";
    }

    config.headers["Accept"] = "application/json";
    config.headers["Device-Type"] = 2;

    return config;
  },
  (error) => Promise.reject(error),
);

// Handle EXPIRED TOKEN globally
let isSessionExpiredToastShown = false;

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Only once per tab
      if (!isSessionExpiredToastShown) {
        isSessionExpiredToastShown = true;

        toast.error("Session expired. Please login again.");

        // Clear token ONLY in this tab
        localStorage.removeItem("usertoken");

        setTimeout(() => {
          window.location.href = "/login";
        }, 800);
      }
    }

    return Promise.reject(error);
  },
);

// ---------------------------------------------------------
// ⭐ LOGIN API
// ---------------------------------------------------------

export const varifyCaptcha = createAsyncThunk(
  "login/varifyCaptcha",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post("/verify-captcha", payload);
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Something went wrong",
      );
    }
  },
);

export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async ({ username, password }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post(`/admin/login`, {
        username: username.trim(),
        password: password.trim(),
      });

      if (!res.data.status && !res.data.success) {
        return rejectWithValue(res.data.message || "Invalid credentials");
      }

      return res.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message,
      );
    }
  },
);

// ---------------------------------------------------------
// ⭐ LOGOUT API
// ---------------------------------------------------------
export const logoutUser = createAsyncThunk(
  "auth/logoutUser",
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post(`/admin/logout`, {});
      return res.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message,
      );
    }
  },
);

// ---------------------------------------------------------
// ⭐ AUTH SLICE
// ---------------------------------------------------------
const initialToken = localStorage.getItem("usertoken");

const authSlice = createSlice({
  name: "auth",
  initialState: {
    user: null,
    token: initialToken || null,
    loading: false,
    error: null,
  },

  reducers: {
    logout: (state) => {
      state.user = null;
      state.token = null;
      localStorage.removeItem("usertoken");
    },

    loadUserFromStorage: (state) => {
      const token = localStorage.getItem("usertoken");
      if (token) state.token = token;
    },
  },

  extraReducers: (builder) => {
    builder
      // ---------------- LOGIN ----------------
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.data;
        state.token = action.payload.data.userAccessToken;

        localStorage.setItem("usertoken", state.token);
      })

      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.user = null;
        state.token = null;
        state.error = action.payload;
      })

      // ---------------- LOGOUT ----------------
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        localStorage.removeItem("usertoken");
      });
  },
});

export const { logout, loadUserFromStorage } = authSlice.actions;
export default authSlice.reducer;
